
import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// RTK slices
import { setToken, setUserIdentity, resetInitialization, setInitializing, setInitialized } from './redux/slices/appSlice';
import { authFetch } from './utils/authFetch';

import { Route, Routes, Navigate } from 'react-router-dom';
import { AppShell, Transition } from '@mantine/core';
import { useAuth0 } from '@auth0/auth0-react';
import { FullPageLoader } from './riskManager/components/HuginnLoader';
import UniversalFileUploader from './UniversalFileUploader.jsx';
import {jwtDecode} from 'jwt-decode';
import {
  isRiskManager, hasAuthorizedRole, isAgentOwner, isReviewer, isPlatformAdminRole,
} from './utils/roleHelpers';
import { Header } from './components/Header';
import useInactivityTimeout from './hooks/useInactivityTimeout';
import './App.css';

// The RiskManager shell (docs/SCREENS.md "Global shell") plus the inherited pages it reuses.
// The ServiceDesk assessment/agency world was deleted in the legacy-deletion slice
// (founder call, 2026-08-14); what survives of it is Admin, the PDF report pages and the
// R5 finding page (ViolationAssessment — legacy name, live content). Lazy-loaded.
const RiskManagerLayout = React.lazy(() => import('./riskManager/components/RiskManagerLayout'));
const RiskDashboard = React.lazy(() => import('./riskManager/pages/RiskDashboard'));
const FindingsPage = React.lazy(() => import('./riskManager/pages/FindingsPage'));
const ReportPage = React.lazy(() => import('./riskManager/pages/ReportPage'));
const ReportsList = React.lazy(() => import('./riskManager/pages/ReportsList'));
const SkillsPage = React.lazy(() => import('./riskManager/pages/SkillsPage'));
const HuginnSoulPage = React.lazy(() => import('./riskManager/pages/HuginnSoulPage'));
const IntegrationsPage = React.lazy(() => import('./riskManager/pages/IntegrationsPage'));
const AdminPage = React.lazy(() => import('./riskManager/pages/AdminPage'));
const AgentRegister = React.lazy(() => import('./riskManager/pages/AgentRegister'));
const DiscoveryRunDetail = React.lazy(() => import('./riskManager/pages/DiscoveryRunDetail'));
const EvidenceLibrary = React.lazy(() => import('./riskManager/pages/EvidenceLibrary'));
const AgentDetail = React.lazy(() => import('./riskManager/pages/AgentDetail'));
const MandateDetail = React.lazy(() => import('./riskManager/pages/MandateDetail'));
const NewMandate = React.lazy(() => import('./riskManager/pages/NewMandate'));
const MandateLibrary = React.lazy(() => import('./riskManager/pages/MandateLibrary'));
const NewMandateStart = React.lazy(() => import('./riskManager/pages/NewMandateStart'));
const PickTemplate = React.lazy(() => import('./riskManager/pages/PickTemplate'));
const MandateCheck = React.lazy(() => import('./riskManager/pages/MandateCheck'));
const MandateSettings = React.lazy(() => import('./riskManager/pages/MandateSettings'));
const MyAgents = React.lazy(() => import('./riskManager/pages/MyAgents'));
const ViolationAssessment = React.lazy(() => import('./riskManager/pages/ViolationAssessment'));
const SkillEditorPage = React.lazy(() => import('./riskManager/pages/SkillEditorPage'));
const KnowledgeBasePage = React.lazy(() => import('./riskManager/pages/KnowledgeBasePage'));
const PosturePage = React.lazy(() => import('./riskManager/pages/PosturePage'));

// Screens the RiskManager shell reaches that are not built yet render <Placeholder>, which
// names the phase that builds them (docs/SCREENS.md screen numbers included).
const Placeholder = React.lazy(() => import('./riskManager/pages/Placeholder'));
const EvidenceSetup = React.lazy(() => import('./riskManager/pages/EvidenceSetup'));

function App() {
  const { user, isAuthenticated, isLoading, logout, getAccessTokenSilently} = useAuth0();

  const appInitializing = useSelector(state => state.app.isInitializing);
  const token = useSelector(state => state.app.token);
  const userIdentity = useSelector(state => state.app.userIdentity);
  const identityLoaded = useSelector(state => state.app.identityLoaded);

  const [userRoles, setUserRoles] = useState([]);
  const [fadeIn, setFadeIn] = useState(false);
  const showInitialLoadingScreen = isLoading || appInitializing;
  const userIsRiskManager = useMemo(() => isRiskManager(userRoles), [userRoles]);
  const userIsAuthorized = useMemo(() => hasAuthorizedRole(userRoles), [userRoles]);
  // First line and nothing else. Same rule as the nav (RiskManagerLayout): owner AND
  // something second-line counts as second line.
  const userIsOwnerOnly = useMemo(
    () => isAgentOwner(userRoles)
      && !isReviewer(userRoles) && !isRiskManager(userRoles) && !isPlatformAdminRole(userRoles),
    [userRoles],
  );
  const dispatch = useDispatch();
  useInactivityTimeout();

  useEffect(() => {
    const initializeApp = async () => {
      try {
        dispatch(resetInitialization());
        dispatch(setInitializing(true));

        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_AUTH0_AUDIENCE || 'auth.agenthuginn.ai',
            // This is THE token: it goes into Redux and authFetch sends it on every backend
            // call, so it is what withAuth/getUserFromToken validates.
            //
            // It previously requested only 'read:models write:models delete:models' — no
            // openid, profile, email or offline_access. Consequences:
            //   * getUserFromToken finds no `email` claim, then falls back to /userinfo,
            //     which requires the `openid` scope and 401s without it. So user.email was
            //     null, and whoami wrote a null email into user_directory — the very column
            //     the admin roster lists people by.
            //   * a third distinct scope set meant a third separately-cached token.
            // Kept identical to src/index.js and the fetchRoles call below.
            scope: 'openid profile email offline_access read:models create:models delete:models',
          }
        });

        dispatch(setToken(token));
        dispatch(setInitialized(true));
      } catch (error) {
        console.error('Error initializing app:', error);
        dispatch(setInitialized(true));
        if (error.error === 'login_required' || error.error === 'consent_required') {
          logout({ logoutParams: { returnTo: window.location.origin } });
        }
      }
    };

    if (isAuthenticated) {
      initializeApp();
    }
  }, [dispatch, getAccessTokenSilently, isAuthenticated, logout]);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_AUTH0_AUDIENCE || 'auth.agenthuginn.ai',
            // Must match src/index.js and the scopes the auth.agenthuginn.ai API defines.
            // This asked for `write:models` (undefined on the API) and omitted
            // `offline_access` — so a silent renewal here requested a DIFFERENT scope set
            // than the initial login, which can force a fresh authorization round-trip
            // instead of using the cached token.
            scope: 'openid profile email offline_access read:models create:models delete:models',
          }
        });

        const decoded = jwtDecode(token);
        const roles = decoded['https://agenthuginn.ai/claims/roles'] || decoded['https://riskgpt.io/claims/roles'] || [];
        setUserRoles(roles);
        console.log('User roles:', roles);
      } catch (error) {
        console.error('Error fetching roles:', error);
      }
    };

    if (isAuthenticated) {
      fetchRoles();
    }
  }, [isAuthenticated, getAccessTokenSilently]);





  // Resolve identity + tenancy once the token is available. whoami records
  // the user in the platform roster and returns their org (null until an admin
  // assigns one) plus isPlatformAdmin.
  useEffect(() => {
    const loadIdentity = async () => {
      try {
        const res = await authFetch('/.netlify/functions/whoami');
        if (!res.ok) throw new Error(`whoami failed (${res.status})`);
        const data = await res.json();
        dispatch(setUserIdentity(data));
      } catch (error) {
        console.error('Error loading identity:', error);
        // Mark loaded with no org so the UI can show the awaiting-access state
        // rather than spinning forever.
        dispatch(setUserIdentity(null));
      }
    };
    if (isAuthenticated && token) {
      loadIdentity();
    }
  }, [isAuthenticated, token, dispatch]);

  useEffect(() => {
    if (!showInitialLoadingScreen) {
      setFadeIn(true);
    }
  }, [showInitialLoadingScreen]);

  // Initial app load only — subsequent data refreshes (e.g. after creating an assessment) keep
  // the shell on screen and load in place.
  //
  // This used to be a scripted greeting: logo, then a chat bubble typing "Hi, I'm Huginn / I'm
  // here to help you build safer AI / Let's get to work", gated behind a four-second minimum so
  // the sequence could finish even when the data had already arrived. It made every cold start
  // four seconds long to say nothing the user needed, and it framed a governance product as a
  // chatbot. The mark beats until the data lands, and that is all.
  if (showInitialLoadingScreen) {
    return <FullPageLoader />;
  }

  return (
    <div className="App">
    <AppShell
      header={{ height: 48 }}
      padding="md"
      styles={{
        // --rm-bg, not the #F5F5F5 that was here: a warm-neutral grey a shade off the token every
        // other surface uses. Small enough to never notice, big enough that the shell and the
        // cards it holds were mixing two greys.
        main: { backgroundColor: 'var(--rm-bg)' }
      }}
    >
      <AppShell.Header>
        <Header
          user={user}
          isAuthenticated={isAuthenticated}
          userRoles={userRoles}
          userIsRiskManager={userIsRiskManager}
          onLogout={() => logout({ logoutParams: { returnTo: window.location.origin } })}
        />
      </AppShell.Header>

      <AppShell.Main>
        <Transition mounted={fadeIn} transition="fade" duration={800} timingFunction="ease">
          {(styles) => (
            <div style={styles}>
              {/* Gate: only users with an explicit role can access the app */}
              {isAuthenticated && userRoles.length > 0 && !userIsAuthorized ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', textAlign: 'center' }}>
                  <div>
                    <h2 style={{ margin: '0 0 8px', fontWeight: 600 }}>Access Denied</h2>
                    <p style={{ color: '#868e96', margin: '0 0 16px' }}>
                      Your account does not have an authorized role. Contact your administrator to request access.
                    </p>
                    <button
                      onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
                      style={{ padding: '8px 20px', cursor: 'pointer', borderRadius: 6, border: '1px solid #dee2e6', background: '#fff' }}
                    >
                      Sign out
                    </button>
                  </div>
                </div>
              ) : isAuthenticated && identityLoaded && !userIdentity?.orgId && !userIdentity?.isPlatformAdmin ? (
                /* Gate: authenticated but not yet assigned to an org */
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', textAlign: 'center' }}>
                  <div>
                    <h2 style={{ margin: '0 0 8px', fontWeight: 600 }}>Awaiting access</h2>
                    <p style={{ color: '#868e96', margin: '0 0 16px', maxWidth: 420 }}>
                      Your account isn’t assigned to an organisation yet. An administrator
                      will add you shortly — please check back soon.
                    </p>
                    <button
                      onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
                      style={{ padding: '8px 20px', cursor: 'pointer', borderRadius: 6, border: '1px solid #dee2e6', background: '#fff' }}
                    >
                      Sign out
                    </button>
                  </div>
                </div>
              ) : (
              <Routes>
          {/* ── RiskManager shell ─────────────────────────────────────────────────────
              The product surface, per docs/SCREENS.md. Everything under one layout so the
              nav, counts and page chrome are consistent. Inherited pages are reused where
              SCREENS.md says the screen is "largely inherited"; the rest are placeholders
              that name their phase. ──────────────────────────────────────────────────── */}
          <Route element={
            <React.Suspense fallback={<div style={{ padding: 40 }}>Loading…</div>}>
              <RiskManagerLayout userRoles={userRoles} />
            </React.Suspense>
          }>
            {/* R1. This was falling through to the inherited ServiceDesk home, so every
                session opened on the wrong product — the landing page is the one screen a user
                sees every single time. An owner-only user lands on My agents: the dashboard is
                an org-wide surface, and its endpoint refuses first line anyway. */}
            <Route index element={
              userIsOwnerOnly
                ? <Navigate to="/my-agents" replace />
                : (
                  <React.Suspense fallback={<div style={{ padding: 40 }}>Loading…</div>}>
                    <RiskDashboard />
                  </React.Suspense>
                )
            } />
            {/* SCREENS.md 02 and 03. The runs route is declared BEFORE /register/:agentId so
                "runs" is never read as an agent id. */}
            <Route path="/register" element={
              <React.Suspense fallback={null}><AgentRegister /></React.Suspense>
            } />
            <Route path="/evidence" element={
              <React.Suspense fallback={null}><EvidenceLibrary /></React.Suspense>
            } />
            <Route path="/register/runs" element={
              <React.Suspense fallback={null}><DiscoveryRunDetail /></React.Suspense>
            } />
            <Route path="/register/runs/:runId" element={
              <React.Suspense fallback={null}><DiscoveryRunDetail /></React.Suspense>
            } />
            <Route path="/register/:agentId" element={
              <React.Suspense fallback={null}><AgentDetail /></React.Suspense>
            } />
            <Route path="/register/:agentId/:tab" element={
              <React.Suspense fallback={null}><AgentDetail /></React.Suspense>
            } />
            {/* One finding, worked inside the agent that raised it. A reviewer who clicks a
                finding on an agent came to work that agent — /findings/:id would take the
                context away, so R5 renders in the tab and the URL follows the reader. */}
            <Route path="/register/:agentId/:tab/:subId" element={
              <React.Suspense fallback={null}><AgentDetail /></React.Suspense>
            } />
            {/* The consolidation (2026-08-10): Mandates, Workbench and Adherence were
                projections of the register, the dashboard and findings under their own nouns.
                The lists died; the mandate DETAIL routes live on. Old paths redirect. */}
            <Route path="/workbench" element={<Navigate to="/" replace />} />
            <Route path="/mandates" element={<Navigate to="/register" replace />} />
            {/* R12 — templates: reusable shapes Risk maintains, never mandates. Standalone. */}
            <Route path="/templates" element={
              <React.Suspense fallback={null}><MandateLibrary /></React.Suspense>
            } />
            <Route path="/mandates/library" element={<Navigate to="/templates" replace />} />
            {/* One component, two voices. The owner route passes isOwner, which removes the
                Sign button entirely rather than disabling it (BRIEF.md §6). */}
            {/* The owner's route in. O2b first, because there is no blank form and no third way
                (BRIEF.md §6): the conversation lives one segment deeper rather than at the top,
                so nobody lands straight in it without being offered the template. */}
            <Route path="/new-mandate/:agentId" element={
              <React.Suspense fallback={null}><NewMandateStart /></React.Suspense>
            } />
            {/* O2c — pick a template. */}
            <Route path="/new-mandate/:agentId/template" element={
              <React.Suspense fallback={null}><PickTemplate /></React.Suspense>
            } />
            {/* O3 — the drafting conversation. Owner-facing, reached from My agents, never
                from the register. */}
            <Route path="/new-mandate/:agentId/huginn" element={
              <React.Suspense fallback={null}><NewMandate /></React.Suspense>
            } />
            <Route path="/mandates/:mandateId" element={
              <React.Suspense fallback={null}><MandateDetail /></React.Suspense>
            } />
            {/* O3b — check before sending. Declared before :mandateId for the same reason
                /mandates/library is: a static segment must not be readable as an id. */}
            <Route path="/my-mandates/:mandateId/check" element={
              <React.Suspense fallback={null}><MandateCheck /></React.Suspense>
            } />
            <Route path="/my-mandates/:mandateId" element={
              <React.Suspense fallback={null}><MandateDetail isOwner /></React.Suspense>
            } />
            {/* Adherence remains as findings; the standalone board died with the
                consolidation. The denominator lives on the dashboard and the register. */}
            <Route path="/adherence" element={<Navigate to="/findings" replace />} />
            {/* O1 — the owner's landing screen. NOT the register with a filter: the owner
                never sees the register (SCREENS.md §Roles), so it has its own endpoint that
                never selects an agent it has not matched to the caller. */}
            <Route path="/my-agents" element={
              <React.Suspense fallback={null}><MyAgents /></React.Suspense>
            } />
            {/* /queue was the old workbench's old name. Still lands somewhere real. */}
            <Route path="/queue" element={<Navigate to="/" replace />} />
            <Route path="/findings" element={
              <React.Suspense fallback={null}>
                <FindingsPage />
              </React.Suspense>
            } />
            {/* R5 — working one finding. Second line only: it carries the pattern across
                agents, which is one of the two things only Risk gets (BRIEF.md §7). */}
            <Route path="/findings/:findingId" element={
              <React.Suspense fallback={null}><ViolationAssessment /></React.Suspense>
            } />
            {/* Risk appetite — the estate against the stated appetite, and the one home of
                a running posture review (founder call, 2026-08-20: the dashboard hosts no
                running state). */}
            <Route path="/risk-appetite" element={
              <React.Suspense fallback={null}><PosturePage /></React.Suspense>
            } />
            <Route path="/reports" element={
              <React.Suspense fallback={<div style={{ padding: 40 }}>Loading…</div>}>
                <ReportsList />
              </React.Suspense>
            } />
            {/* The PDF machinery kept for estate reporting (founder call, 2026-08-14).
                Renders one assessment's report; empty until assessments exist again. */}
            <Route path="/reports/:assessmentId" element={
              <React.Suspense fallback={<div style={{ padding: 40 }}>Loading…</div>}>
                <ReportPage />
              </React.Suspense>
            } />
            {/* R8 — Huginn's own skill catalogue. */}
            <Route path="/skills" element={
              <React.Suspense fallback={null}><SkillsPage /></React.Suspense>
            } />
            {/* R7 — the Mandate category page. A category holds its own settings (BRIEF.md §5),
                so this lives under Skills rather than as a top-level "rules" area. */}
            <Route path="/skills/mandate" element={
              <React.Suspense fallback={null}><MandateSettings /></React.Suspense>
            } />
            {/* The skill editor. React Router 6 ranks the static /skills/mandate above this
                dynamic segment, so the two coexist; the editable keys never collide with
                "mandate". A finding's stamp deep-links here as /skills/:key?v=N. */}
            <Route path="/skills/:key" element={
              <React.Suspense fallback={null}><SkillEditorPage /></React.Suspense>
            } />
            {/* The knowledge base — tabs, landing on the overview dashboard (founder call,
                2026-08-11). One sidebar entry; the six surfaces live inside. */}
            <Route path="/knowledge" element={
              <React.Suspense fallback={null}><KnowledgeBasePage /></React.Suspense>
            } />
            <Route path="/knowledge/:tab" element={
              <React.Suspense fallback={null}><KnowledgeBasePage /></React.Suspense>
            } />
            <Route path="/soul" element={
              <React.Suspense fallback={null}><HuginnSoulPage /></React.Suspense>
            } />
            <Route path="/integrations" element={
              <React.Suspense fallback={null}><IntegrationsPage /></React.Suspense>
            } />
            {/* Screen 01. Reached from Integrations, where SCREENS.md 09 puts source health. */}
            <Route path="/setup" element={
              <React.Suspense fallback={null}><EvidenceSetup /></React.Suspense>
            } />
            <Route path="/settings" element={
              <React.Suspense fallback={null}>
                <Placeholder
                  title="Settings"
                  sub="Workspace administration. Operational behaviour is defined in skills, mandates and the knowledge base."
                  phase="any phase"
                  screen="16"
                  detail="Deliberately thin — users and roles, data residency, retention, audit-log export."
                />
              </React.Suspense>
            } />
            {/* Platform admin — org and user management. Shifted out of the legacy /agency
                shell (founder call, 2026-08-14); the page refuses non-admins itself and
                every admin-* endpoint gates server-side. */}
            <Route path="/admin" element={
              <React.Suspense fallback={<div style={{ padding: 40 }}>Loading Admin…</div>}>
                <AdminPage />
              </React.Suspense>
            } />
          </Route>

          {/* Universal uploader entrypoint (also mounted inside /knowledge/policies) */}
          <Route path="/universal-uploader" element={<UniversalFileUploader />} />

          <Route path="*" element={<div>Not Found</div>} />
          </Routes>
              )}
            </div>
          )}
        </Transition>
      </AppShell.Main>
    </AppShell>
    </div>

  );
}

export default App;
